import { useState } from "react";
import { Box, Loader, Modal, Stack, Stepper } from "@mantine/core";
import {
  getCompletedSteps,
  getItemTypeForProductType,
  getStepsForProduct,
  InlayCoreCharacteristicsStep,
  MaterialCharacteristicsStep,
  ProductPreferencesStep,
  useProductWorkflow,
  type StepKeys,
} from "@jasper/product-workflows";
import { useOrderConfiguration } from "@jasper/use-cases";

type OrderConfiguration = ReturnType<typeof useOrderConfiguration>;
type ConfigurableProduct = NonNullable<
  NonNullable<OrderConfiguration["order"]>["products"]
>[number];

type ProductV2AdapterProps = {
  orderId: string;
  productId: string;
  /* configuration done -> back to v1 recap */
  onComplete: () => void;
  /* closed without finishing -> back  */
  onClose: () => void;
  inline?: boolean;
};

/**
 * V1 -> V2 adapter: renders the V2 product configuration steps inside the V1 funnel,
 * driven by local state instead of the  /configure routes.
 * Generic over the product type (Bridge, Veneer, ...) via the workflow registry
 *
 */

export const ProductV2Adapter = ({
  orderId,
  productId,
  onComplete,
  onClose,
  inline = false,
}: ProductV2AdapterProps) => {
  const orderConfiguration = useOrderConfiguration({ orderId });
  const { order } = orderConfiguration;
  const product = order?.products?.find(p => p?.id === productId);
  const firstStepKey = product
    ? getStepsForProduct(product)[0]?.key
    : undefined;

  const [currentStepKey, setCurrentStepKey] = useState<StepKeys | undefined>(
    undefined
  );

  const body =
    orderConfiguration.isLoading || !product || !firstStepKey ? (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    ) : (
      <ProductV2AdapterContent
        orderId={orderId}
        productId={productId}
        product={product}
        orderConfiguration={orderConfiguration}
        currentStepKey={currentStepKey ?? firstStepKey}
        onGo={setCurrentStepKey}
        onComplete={onComplete}
      />
    );

  if (inline) {
    return (
      <Box h="100%" w="100%">
        {body}
      </Box>
    );
  }

  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen
      withCloseButton
      title="Configuration du produit"
    >
      {body}
    </Modal>
  );
};

type ContentProps = {
  orderId: string;
  productId: string;
  product: ConfigurableProduct;
  orderConfiguration: OrderConfiguration;
  currentStepKey: StepKeys;
  onGo: (stepKey: StepKeys) => void;
  onComplete: () => void;
};

const ProductV2AdapterContent = ({
  orderId,
  productId,
  product,
  orderConfiguration,
  currentStepKey,
  onGo,
  onComplete,
}: ContentProps) => {
  const { currentStep, stepIndex, activeSteps, goNext, goBack, isFirst } =
    useProductWorkflow({
      steps: getStepsForProduct(product),
      orderId,
      productId,
      completedStepKeys: getCompletedSteps(product),
      onComplete,
      currentStepKey,
      onGo: ({ stepKey }) => onGo(stepKey),
      product,
    });

  const onBack = isFirst ? onComplete : goBack;

  const stepProps = {
    orderId,
    productId,
    product,
    orderConfiguration,
    onNext: goNext,
    onBack,
    itemType: getItemTypeForProductType(product.type),
  };

  return (
    <Stack gap="md" h="100%" w="100%" p="md">
      <Stepper active={stepIndex} onStepClick={() => undefined}>
        {activeSteps.map(step => (
          <Stepper.Step key={step.key} label={step.label} />
        ))}
      </Stepper>
      {currentStep.key === "material" && (
        <MaterialCharacteristicsStep {...stepProps} />
      )}
      {currentStep.key === "inlayCore" && (
        <InlayCoreCharacteristicsStep {...stepProps} />
      )}
      {currentStep.key === "preferences" && (
        <ProductPreferencesStep {...stepProps} />
      )}
    </Stack>
  );
};

export default ProductV2Adapter;
